import React from 'react';
import cx from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faCircleInfo, faCircleXmark } from '@fortawesome/pro-solid-svg-icons';
import { faCircle } from '@fortawesome/pro-regular-svg-icons';
import Tooltip from '../../ui-elements/Tooltip/Tooltip';
import {
    ALLOWED_PASSWORD_CHARS,
    MIN_PASSWORD_LENGTH,
} from '../../services/passwordStrength';

interface IProps {
    password: string;
}

const TIERS = ['very-weak', 'weak', 'fair', 'good', 'strong'] as const;
const LABELS = ['Mjög veikt', 'Veikt', 'Sæmilegt', 'Gott', 'Frábært'];
const TOTAL_BARS = 5;

const passwordChecks = (password: string): { label: React.ReactNode; met: boolean }[] => [
    { label: 'Leyfð tákn eru  ! # _ , .', met: ALLOWED_PASSWORD_CHARS.test(password) },
    { label: `Að minnsta kosti ${MIN_PASSWORD_LENGTH} stafir`, met: password.length >= MIN_PASSWORD_LENGTH },
    { label: 'Einn hástaf (A–Ö)', met: /[A-ZÁÐÉÍÓÚÝÞÆÖ]/.test(password) },
    { label: 'Einn lágstaf (a–ö)', met: /[a-záðéíóúýþæö]/.test(password) },
    { label: 'Einn tölustaf (0–9)', met: /\d/.test(password) },
];

const PasswordChecklist: React.FC<IProps> = ({ password }) => {
    const empty = password.length === 0;
    return (
        <ul className="password-checklist">
            {passwordChecks(password).map(({ label, met }, i) => {
                const state = empty ? 'empty' : (met ? 'met' : 'unmet');
                const icon = empty ? faCircle : (met ? faCircleCheck : faCircleXmark);
                return (
                    <li key={i} className={`password-checklist__item password-checklist__item--${state}`}>
                        <FontAwesomeIcon icon={icon} className="password-checklist__icon" />
                        <span>{label}</span>
                    </li>
                );
            })}
        </ul>
    );
};

const PasswordStrengthMeter: React.FC<IProps> = ({ password }) => {
    const empty = password.length === 0;
    const metCount = passwordChecks(password).filter(c => c.met).length;
    const filled = empty ? 0 : Math.max(1, metCount);
    const tierIndex = Math.max(0, filled - 1);
    const tier = empty ? 'empty' : TIERS[tierIndex];
    const label = empty ? '' : LABELS[tierIndex];

    return (
        <div className={cx('KCL_password-strength', `password-strength--${tier}`)}>
            <div className="password-strength__row">
                <div className="password-strength__header">
                    <span className="password-strength__title">Skilyrði lykilorðs</span>
                    <Tooltip
                        noUnderline
                        tooltipClassName="password-strength__tooltip"
                        text={<FontAwesomeIcon icon={faCircleInfo} className="password-strength__info" />}
                        tooltip={<PasswordChecklist password={password} />}
                    />
                </div>
                <div className="password-strength__bars">
                    {Array.from({ length: TOTAL_BARS }).map((_, i) => (
                        <span
                            key={i}
                            className={cx('password-strength__bar', { 'password-strength__bar--filled': i < filled })}
                        />
                    ))}
                </div>
            </div>
            <span className="password-strength__label">{label || ' '}</span>
        </div>
    );
};

export default PasswordStrengthMeter;
